import { InPageUsedTools, PageProject } from "@/apps/page/page-project.interface";
import { PageSection } from "@/apps/page/page.interfaces";
import { computed, reactive, ref, Ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useFormValidation } from "@/shared/utils/forms/useFormValidation";
import { required } from "@/shared/utils/forms/i18n-vuelidators";
import { useToolProjectsStore } from "@/apps/page/project/tool-projects.store";
import {
    makeSkimmedToolProjectData,
    SkimmedToolProjectData,
} from "@/apps/page/project/tool-project-data";

export const TAB_NEW_PROJECT = "new-project";
export const TAB_EXISTING_PROJECT = "existing-project";

export function useBindSectionWithProject(
    props: {
        tool?: InPageUsedTools;
        sectionId?: PageSection["id"];
    },
    {
        page,
        connectToolSectionWithProject,
        bindToolSectionWithProject,
        routeAfterBind = true,
    }: {
        page: Ref<Pick<PageProject, "uuid">>;
        connectToolSectionWithProject: (args: {
            tool?: InPageUsedTools;
            sectionId?: PageSection["id"];
            projectName: string;
        }) => Promise<PageProject["uuid"]>;
        bindToolSectionWithProject: (args: {
            tool?: InPageUsedTools;
            sectionId?: PageSection["id"];
            projectId: PageProject["id"];
            projectUuid: PageProject["uuid"];
            projectData: SkimmedToolProjectData;
        }) => Promise<void>;
        routeAfterBind?: boolean;
    }
) {
    const selectedTab = ref(TAB_EXISTING_PROJECT);
    const router = useRouter();
    const project = reactive({ projectName: "" });
    const { getProjectsByTool } = useToolProjectsStore();
    const { v$, validateForm } = useFormValidation(
        {
            projectName: { required },
        },
        project
    );
    const toolProjects = computed(() =>
        getProjectsByTool(props?.tool).map(({ name, id }) => ({
            label: name,
            value: id,
        }))
    );
    const shouldHaveExistingOption = computed(() => toolProjects.value.length > 0);
    const chosenProjectId = ref(toolProjects.value[0]?.value);

    watch(
        props,
        (props) => {
            const { tool } = props || {};
            if (tool) {
                v$.value.$reset();
                selectedTab.value = shouldHaveExistingOption.value
                    ? TAB_EXISTING_PROJECT
                    : TAB_NEW_PROJECT;
                chosenProjectId.value = toolProjects.value[0]?.value || 0;
            }
        },
        { immediate: true }
    );

    watch(shouldHaveExistingOption, (now) => {
        selectedTab.value = now ? TAB_EXISTING_PROJECT : TAB_NEW_PROJECT;
        chosenProjectId.value = toolProjects.value[0]?.value || 0;
    });

    return {
        selectedTab,
        shouldHaveExistingOption,
        chosenProjectId,
        project,
        toolProjects,
        projectNameError: computed(() => v$.value.projectName.$errors?.[0]?.$message),
        resetForm() {
            v$.value.$reset();
        },
        onFieldBlur() {
            setTimeout(() => {
                v$.value.projectName.$touch();
            }, 200);
        },
        async bindWithProject() {
            const { projectName } = project;
            const { tool, sectionId } = props;
            switch (selectedTab.value) {
                case TAB_NEW_PROJECT:
                    if (await validateForm({ dontScrollToError: true })) {
                        const projectUuid = await connectToolSectionWithProject({
                            tool,
                            sectionId,
                            projectName,
                        });
                        if (projectUuid) {
                            await router.push(
                                `/${tool}/projects/${projectUuid}?page=${page.value.uuid}`
                            );
                        }
                    }
                    break;
                case TAB_EXISTING_PROJECT:
                    // Handle existing project binding logic here
                    if (!chosenProjectId.value) {
                        return;
                    }
                    // eslint-disable-next-line no-case-declarations
                    const project = getProjectsByTool(tool).find(
                        (p) => p.id === chosenProjectId.value
                    );
                    if (project) {
                        await bindToolSectionWithProject({
                            tool,
                            sectionId,
                            projectId: project.id,
                            projectUuid: project.uuid,
                            projectData: makeSkimmedToolProjectData(project, tool),
                        });

                        if (routeAfterBind) {
                            await router.push(
                                `/${tool}/projects/${project.uuid}?page=${page.value.uuid}`
                            );
                        }
                    }
                    break;
                default:
                    new Error("Unknown tab selected");
            }
        },
    };
}
